import {nodeTypeToImage} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/defaults/node-types";

class GenogramCursorManager {

	constructor({cy, editorOptions = {}} = {}) {
		this.cy = cy
		this.editorOptions = editorOptions
		this.cursorConfig = editorOptions.cursorConfig || {}
		this.nodeTypesByType = this.buildNodeTypesByType(editorOptions.nodeTypes || [])
		this.hoveredNode = null
		this.draggedNode = null
		this.isEdgeHandleHovered = false
		this.isEdgeHandleActive = false
		this.isPlacementModeActive = false
		this.placementShape = null
		this.placementPreviewEl = null
		this.lastPointerPosition = null
		this.listeners = []
		this.domListeners = []
	}

	init() {
		if(!this.cy){
			return
		}

		this.bindNodeListeners()
		this.bindEdgeHandleListeners()
		this.bindPointerListeners()
		this.applyCursor()
	}

	destroy() {
		for(let listener of this.listeners){
			if(listener.selector){
				this.cy.removeListener(listener.event, listener.selector, listener.handler)
				continue
			}
			this.cy.removeListener(listener.event, listener.handler)
		}
		this.listeners = []
		this.removeDomListeners()
		this.destroyPlacementPreview()
		this.resetState()
		this.applyCursor()
	}

	bindNodeListeners() {
		this.addCyListener('mouseover', 'node', event => {
			let node = event.target
			if(this.isEdgeHandleNode(node)){
				return
			}
			this.hoveredNode = node
			this.applyCursor()
		})
		this.addCyListener('mouseout', 'node', event => {
			let node = event.target
			if(this.isEdgeHandleNode(node)){
				return
			}
			if(this.hoveredNode?.same(node)){
				this.hoveredNode = null
			}
			this.applyCursor()
		})
		this.addCyListener('grab', 'node', event => {
			let node = event.target
			if(this.isEdgeHandleNode(node)){
				return
			}
			this.draggedNode = node
			this.applyCursor()
		})
		this.addCyListener('free', 'node', event => {
			let node = event.target
			if(this.isEdgeHandleNode(node)){
				return
			}
			if(this.draggedNode?.same(node)){
				this.draggedNode = null
			}
			this.applyCursor()
		})
	}

	bindEdgeHandleListeners() {
		this.addCyListener('mouseover', 'node.eh-handle', () => {
			this.isEdgeHandleHovered = true
			this.applyCursor()
		})
		this.addCyListener('mouseout', 'node.eh-handle', () => {
			this.isEdgeHandleHovered = false
			this.applyCursor()
		})
		this.addCyListener('ehstart', () => {
			this.isEdgeHandleActive = true
			this.applyCursor()
		})
		this.addCyListener('ehstop ehcancel ehcomplete', () => {
			this.isEdgeHandleActive = false
			this.applyCursor()
		})
		this.addCyListener('ehhide', () => {
			this.isEdgeHandleHovered = false
			this.isEdgeHandleActive = false
			this.applyCursor()
		})
	}

	bindPointerListeners() {
		if(typeof document === "undefined"){
			return
		}

		this.addDomListener(document, 'mousemove', event => {
			this.lastPointerPosition = {
				x: event.clientX,
				y: event.clientY,
			}
			this.updatePlacementPreviewPosition()
		})
	}

	addCyListener(event, selector, handler) {
		let listener = {event, selector, handler}
		this.listeners.push(listener)
		if(selector){
			this.cy.on(event, selector, handler)
			return
		}
		this.cy.on(event, handler)
	}

	addDomListener(target, event, handler) {
		if(!target?.addEventListener){
			return
		}

		this.domListeners.push({target, event, handler})
		target.addEventListener(event, handler)
	}

	removeDomListeners() {
		for(let listener of this.domListeners){
			listener.target?.removeEventListener?.(listener.event, listener.handler)
		}
		this.domListeners = []
	}

	buildNodeTypesByType(nodeTypes) {
		return nodeTypes.reduce((acc, nodeType) => {
			if(nodeType?.type){
				acc[nodeType.type] = nodeType
			}
			return acc
		}, {})
	}

	resolveNodeCursor(node, configKey, overrideKey) {
		if(!node){
			return ""
		}

		let nodeData = typeof node.data === "function" ? node.data() : node.data || {}
		let nodeType = this.nodeTypesByType[nodeData.type] || {}
		return nodeData[overrideKey] || nodeType[overrideKey] || this.cursorConfig[configKey] || ""
	}

	isEdgeHandleNode(node) {
		return !!node?.hasClass?.('eh-handle')
	}

	resetState() {
		this.hoveredNode = null
		this.draggedNode = null
		this.isEdgeHandleHovered = false
		this.isEdgeHandleActive = false
		this.isPlacementModeActive = false
		this.placementShape = null
		this.lastPointerPosition = null
	}

	startPlacementMode(shape, sourceEvent = null) {
		this.isPlacementModeActive = true
		this.placementShape = shape || null
		this.capturePointerPosition(sourceEvent)
		this.renderPlacementPreview()
		this.applyCursor()
	}

	stopPlacementMode() {
		this.isPlacementModeActive = false
		this.placementShape = null
		this.destroyPlacementPreview()
		this.applyCursor()
	}

	applyCursor() {
		let cursor = this.getActiveCursor()
		let container = this.cy?.container?.()
		if(!container){
			return
		}
		container.classList.toggle('genogram-cursor--placement', this.isPlacementModeActive)
		container.style.cursor = cursor || ""
	}

	getActiveCursor() {
		if(this.isPlacementModeActive){
			return this.cursorConfig.placementCursor || this.cursorConfig.defaultCursor
		}
		if(this.isEdgeHandleActive){
			return this.cursorConfig.edgeHandleActiveCursor
		}
		if(this.isEdgeHandleHovered){
			return this.cursorConfig.edgeHandleHoverCursor
		}
		if(this.draggedNode?.grabbable?.()){
			return this.resolveNodeCursor(this.draggedNode, 'nodeDragCursor', 'dragCursor')
		}
		if(this.hoveredNode?.grabbable?.()){
			return this.resolveNodeCursor(this.hoveredNode, 'nodeHoverCursor', 'hoverCursor')
		}
		return this.cursorConfig.defaultCursor || ""
	}

	capturePointerPosition(sourceEvent = null) {
		let clientX = sourceEvent?.clientX
		let clientY = sourceEvent?.clientY
		if(clientX == null || clientY == null){
			clientX = sourceEvent?.touches?.[0]?.clientX
			clientY = sourceEvent?.touches?.[0]?.clientY
		}
		if(clientX == null || clientY == null){
			return
		}

		this.lastPointerPosition = {x: clientX, y: clientY}
	}

	renderPlacementPreview() {
		if(!this.shouldShowPlacementPreview()){
			this.destroyPlacementPreview()
			return
		}

		if(!this.placementPreviewEl){
			this.placementPreviewEl = this.createPlacementPreviewElement()
			document.body.appendChild(this.placementPreviewEl)
		}
		this.syncPlacementPreviewContent()
		this.updatePlacementPreviewPosition()
	}

	shouldShowPlacementPreview() {
		let supportsFinePointer = true
		if(typeof window !== "undefined" && typeof window.matchMedia === "function"){
			supportsFinePointer = window.matchMedia("(pointer: fine)").matches
		}
		return this.isPlacementModeActive && supportsFinePointer
	}

	createPlacementPreviewElement() {
		let previewEl = document.createElement('div')
		let img = document.createElement('img')
		let label = document.createElement('div')

		previewEl.className = 'genogram-placement-preview'
		previewEl.style.position = 'fixed'
		previewEl.style.left = '0'
		previewEl.style.top = '0'
		previewEl.style.zIndex = '100000'
		previewEl.style.pointerEvents = 'none'
		previewEl.style.transform = 'translate(14px, calc(-100% - 18px))'
		previewEl.style.background = 'rgba(247, 249, 251, 0.96)'
		previewEl.style.border = '1px solid #DCE3E8'
		previewEl.style.borderRadius = '10px'
		previewEl.style.padding = '10px 12px'
		previewEl.style.boxShadow = '0 10px 24px rgba(16, 24, 40, 0.12)'
		previewEl.style.display = 'flex'
		previewEl.style.flexDirection = 'column'
		previewEl.style.alignItems = 'center'
		previewEl.style.gap = '6px'
		previewEl.style.opacity = '1'

		img.className = 'genogram-placement-preview__image'
		img.style.width = '56px'
		img.style.height = '56px'
		img.style.objectFit = 'contain'

		label.className = 'genogram-placement-preview__label'
		label.style.color = '#1C2434'
		label.style.fontSize = '12px'
		label.style.fontWeight = '600'
		label.style.textAlign = 'center'
		label.style.maxWidth = '110px'
		label.style.lineHeight = '1.25'

		previewEl.appendChild(img)
		previewEl.appendChild(label)
		return previewEl
	}

	syncPlacementPreviewContent() {
		let previewEl = this.placementPreviewEl
		if(!previewEl){
			return
		}

		let img = previewEl.querySelector('.genogram-placement-preview__image')
		let label = previewEl.querySelector('.genogram-placement-preview__label')
		let labelText = this.formatPlacementShapeLabel(this.placementShape)
		let imageSrc = this.resolvePlacementShapeImage(this.placementShape)

		if(img){
			img.src = imageSrc
			img.alt = labelText
			img.style.display = imageSrc ? 'block' : 'none'
		}
		if(label){
			label.textContent = labelText
			label.style.display = labelText ? 'block' : 'none'
		}
	}

	resolvePlacementShapeImage(shape = null) {
		return shape?.src || nodeTypeToImage[shape?.type] || ""
	}

	formatPlacementShapeLabel(shape = null) {
		return String(shape?.label || shape?.name || "").replaceAll("<br/>", " ").trim()
	}

	updatePlacementPreviewPosition() {
		if(!this.placementPreviewEl || !this.lastPointerPosition){
			return
		}

		this.placementPreviewEl.style.left = `${this.lastPointerPosition.x}px`
		this.placementPreviewEl.style.top = `${this.lastPointerPosition.y}px`
	}

	destroyPlacementPreview() {
		this.placementPreviewEl?.remove?.()
		this.placementPreviewEl = null
	}
}

export default GenogramCursorManager
